/**
 * Pure helpers for the MFA inputs and the backup-codes hand-off.
 * No React / DOM dependency.
 */

export const TOTP_LENGTH = 6
export const BACKUP_CODE_GROUP = 4
/** Spec §5: A–Z without I/O, digits 2–9. Used only for client-side normalisation/validation. */
export const BACKUP_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

const TOTP_RE = /^[0-9]{6}$/
const BACKUP_RE = /^[A-Z0-9]{4}-[A-Z0-9]{4}$/

/** Keep digits only, cap at 6. Tolerates pasted values with spaces/dashes. */
export function sanitizeTotpInput(raw: string): string {
  return (raw || '').replace(/[^0-9]/g, '').slice(0, TOTP_LENGTH)
}

export function isCompleteTotp(code: string): boolean {
  return TOTP_RE.test(code)
}

/**
 * Normalise a typed/pasted backup code to `XXXX-XXXX` while typing
 * (partial values allowed, e.g. "AB", "ABCD-E"). Upper-cases, drops anything
 * outside A–Z / 0–9, caps at 8 characters, inserts the dash after 4.
 */
export function formatBackupCodeInput(raw: string): string {
  const chars = (raw || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, BACKUP_CODE_GROUP * 2)
  if (chars.length <= BACKUP_CODE_GROUP) return chars
  return `${chars.slice(0, BACKUP_CODE_GROUP)}-${chars.slice(BACKUP_CODE_GROUP)}`
}

export function isCompleteBackupCode(code: string): boolean {
  return BACKUP_RE.test(code)
}

export interface BackupCodesTextOptions {
  email?: string | null
  issuedAt?: Date
}

/** Body of the downloadable `.txt` file. Plain ASCII so it opens anywhere. */
export function buildBackupCodesText(codes: string[], opts: BackupCodesTextOptions = {}): string {
  const issued = opts.issuedAt || new Date()
  const lines: string[] = [
    'Global Coatings Admin Panel - two-step verification backup codes',
    '',
  ]
  if (opts.email) lines.push(`Account: ${opts.email}`)
  lines.push(`Generated: ${issued.toISOString()}`)
  lines.push('')
  lines.push('Each code can be used ONCE instead of a code from your authenticator app.')
  lines.push('Keep this file somewhere safe and private. Anyone with these codes can sign in as you.')
  lines.push('')
  codes.forEach((code, i) => {
    lines.push(`${String(i + 1).padStart(2, ' ')}. ${code}`)
  })
  lines.push('')
  return lines.join('\n')
}

export function backupCodesFilename(issuedAt: Date = new Date()): string {
  const y = issuedAt.getFullYear()
  const m = String(issuedAt.getMonth() + 1).padStart(2, '0')
  const d = String(issuedAt.getDate()).padStart(2, '0')
  return `global-coatings-admin-backup-codes-${y}-${m}-${d}.txt`
}
